import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../app/store";
import { AppStorage } from "../features/storage/appStorage";
import { toDate } from "../utils/dateValueConversion";

export const userSliceState = (state: RootState) => state.user;

interface UserState {
    isAuthenticated: boolean;
    email: string | null;
    roles: string[];
    token: string | null;
    tokenExpiry: string | null;
}

export type UserLoginInfo = {
    email: string;
    roles: string[];
    token: string;
    tokenExpiry: Date;
};

const initialState: UserState = {
    isAuthenticated: AppStorage.isAuthenticated(),
    email: AppStorage.getUserEmail(),
    roles: AppStorage.getUserRoles(),
    token: AppStorage.getAuthToken(),
    tokenExpiry: AppStorage.getAuthTokenExpiry()?.toISOString() ?? null
};

const clearUserState = (state: UserState): void => {
    state.isAuthenticated = false;
    state.email = null;
    state.roles = [];
    state.token = null;
    state.tokenExpiry = null;
};

const userSlice = createSlice({
    name: "user",
    initialState,
    reducers: {
        login: (state, action: PayloadAction<UserLoginInfo>) => {
            const expiry = toDate(action.payload.tokenExpiry);
            if (!expiry) {
                clearUserState(state);
                return;
            }

            state.isAuthenticated = action.payload.token != null;
            state.email = action.payload.email;
            state.token = action.payload.token;
            state.roles = action.payload.roles;
            state.tokenExpiry = expiry.toISOString();
        },
        logout: state => {
            clearUserState(state);
        }
    }
});

export const { logout, login } = userSlice.actions;
export default userSlice.reducer;
